import React, { useState, useEffect } from 'react';
import { paymentsAPI, executiveOfficersAPI, branchesAPI, clientsAPI, vendorsAPI, returnsAPI, companyAccountsAPI } from '../utils/api';
import '../css/PaymentManagement.css';

const PaymentManagement = () => {
    const [activeTab, setActiveTab] = useState('sales');
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [executives, setExecutives] = useState([]);

    // Inline panel + modal states for adding payments
    const [showPartyPanel, setShowPartyPanel] = useState(false);
    const [showPaymentFormModal, setShowPaymentFormModal] = useState(false);
    const [selectedParty, setSelectedParty] = useState(null);
    const [partySearch, setPartySearch] = useState('');

    // Editing states for payments
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({
        amount_paid: '',
        account_paid: '',
        reference_number: '',
        notes: ''
    });

    // Data states for parties
    const [customers, setCustomers] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [filteredParties, setFilteredParties] = useState([]);
    const [loadingParties, setLoadingParties] = useState(false);

    // New Payment Form state
    const [paymentForm, setPaymentForm] = useState({
        transaction_type: 'payment',
        payment_mode: 'cash', // cash, bank_transfer, cheque, upi
        transaction_date: new Date().toISOString().split('T')[0],
        amount_paid: '',
        account_paid: '', // e.g. which bank
        reference_number: '',
        notes: '',
        executive_name: '',
        branch_name: ''
    });

    // Filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [paymentType, setPaymentType] = useState('all');
    const [executiveName, setExecutiveName] = useState('');
    const [branchName, setBranchName] = useState('');
    const [customerName, setCustomerName] = useState('');
    const [branches, setBranches] = useState([]);
    const [companyAccounts, setCompanyAccounts] = useState([]);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [selectedIds, setSelectedIds] = useState([]);
    const limit = 25;

    useEffect(() => {
        fetchExecutives();
        fetchBranches();
        fetchCompanyAccounts();
    }, []);

    useEffect(() => {
        fetchPayments();
    }, [activeTab, page, startDate, endDate, paymentType, executiveName, branchName, customerName]);

    const fetchExecutives = async () => {
        try {
            const response = await executiveOfficersAPI.getAll();
            if (response.data && Array.isArray(response.data)) {
                setExecutives(response.data);
            }
        } catch (err) {
            // console.error('Error fetching executives:', err);
        }
    };

    const fetchCompanyAccounts = async () => {
        try {
            const response = await companyAccountsAPI.getAll();
            if (response.data && response.data.accounts) {
                setCompanyAccounts(response.data.accounts);
            } else if (response.data && Array.isArray(response.data)) {
                setCompanyAccounts(response.data);
            }
        } catch (error) {
            // console.error('Error fetching company accounts:', error);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await branchesAPI.getAll();
            if (response.data && response.data.success) {
                setBranches(response.data.branches || []);
            }
        } catch (error) {
            // console.error('Error fetching branches:', error);
        }
    };

    const fetchPayments = async () => {
        setLoading(true);
        setError(null);
        setPayments([]); // clear stale data immediately on each fetch
        try {
            // Only send params that have real values — skip blanks and 'all'
            const params = { page, limit };
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;
            if (paymentType && paymentType !== 'all') params.paymentType = paymentType;
            if (executiveName) params.executiveName = executiveName;
            if (branchName) params.branchName = branchName;
            if (customerName) params.customerName = customerName;

            const response = activeTab === 'sales'
                ? await paymentsAPI.getSalesPayments(params)
                : await paymentsAPI.getPurchasePayments(params);

            if (response.data.success) {
                setPayments(response.data.payments);
                setTotalPages(response.data.pagination.totalPages);
                setTotalRecords(response.data.pagination.total);
            }
        } catch (err) {
            // console.error('Error fetching payments:', err);
            setError('Failed to load payments. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(1);
        setSelectedIds([]);
        setShowPartyPanel(false);
    };

    const clearFilters = () => {
        setStartDate('');
        setEndDate('');
        setPaymentType('all');
        setExecutiveName('');
        setBranchName('');
        setCustomerName('');
        setPage(1);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(payments.map(p => p.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const handleDeleteSelected = async () => {
        if (!selectedIds.length) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected payment(s)?`)) return;

        try {
            setLoading(true);
            const deleteAPI = activeTab === 'sales' ? clientsAPI.deleteTransaction : vendorsAPI.deleteTransaction;
            for (const id of selectedIds) {
                await deleteAPI(id);
            }
            setSelectedIds([]);
            fetchPayments();
            alert('Selected payments deleted successfully');
        } catch (err) {
            alert('Failed to delete some payments');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const d = new Date(dateString);
        if (isNaN(d.getTime()) || d.getFullYear() < 1900) return 'N/A';
        return d.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchParties = async (tab) => {
        setLoadingParties(true);
        try {
            if (tab === 'sales') {
                const response = await returnsAPI.getByCustomer();
                let clientList = [];
                if (response.data && Array.isArray(response.data)) {
                    clientList = response.data;
                } else if (response.data && response.data.customers) {
                    clientList = response.data.customers;
                } else if (response.data && response.data.data) {
                    clientList = response.data.data;
                }
                const transformed = clientList.map(c => ({
                    id: c.id || c.customer_id,
                    name: c.name || c.customer_name || 'Unknown',
                    phone: c.phone || c.contact || c.mobile || '',
                    address: c.address || '',
                    executive_officer_name: c.executive_officer_name || '',
                    type: 'Customer'
                }));
                setCustomers(transformed);
                setFilteredParties(transformed);
            } else {
                const response = await vendorsAPI.getAll();
                const vendorList = response.data?.data || response.data || [];
                const transformed = vendorList.map(v => ({
                    id: v.id || v.vendor_id,
                    name: v.name || v.vendor_name || 'Unknown',
                    phone: v.phone || v.mobile || '',
                    address: v.address || '',
                    executive_officer_name: v.executive_officer_name || '',
                    type: 'Vendor'
                }));
                setVendors(transformed);
                setFilteredParties(transformed);
            }
        } catch (error) {
            console.error('Error fetching parties:', error);
            alert('Failed to load customers/vendors. Please try again.');
        } finally {
            setLoadingParties(false);
        }
    };

    useEffect(() => {
        const sourceData = activeTab === 'sales' ? customers : vendors;
        if (sourceData.length > 0 && partySearch) {
            const filtered = sourceData.filter(item => {
                const searchLower = partySearch.toLowerCase();
                return (
                    item.name?.toLowerCase().includes(searchLower) ||
                    item.phone?.includes(partySearch)
                );
            });
            setFilteredParties(filtered);
        } else {
            setFilteredParties(sourceData);
        }
    }, [partySearch, customers, vendors, activeTab]);

    const handlePartySelect = (party) => {
        setSelectedParty(party);
        setShowPartyPanel(false);
        setPaymentForm(prev => ({
            ...prev,
            transaction_date: new Date().toISOString().split('T')[0],
            // Auto-fill executive officer from the client's assigned officer
            executive_name: party.executive_officer_name || prev.executive_name || ''
        }));
        setShowPaymentFormModal(true);
    };

    const handleSavePayment = async () => {
        try {
            if (!paymentForm.amount_paid || isNaN(paymentForm.amount_paid) || Number(paymentForm.amount_paid) <= 0) {
                alert("Please enter a valid payment amount.");
                return;
            }

            setLoading(true);
            const amt = Number(paymentForm.amount_paid);

            const payload = {
                client_id: selectedParty.id,
                transaction_type: 'payment',
                payment_mode: paymentForm.payment_mode || 'cash',
                amount: 0,
                total_amount: 0,
                amount_paid: amt,
                account_paid: paymentForm.account_paid || '',
                transaction_date: paymentForm.transaction_date,
                reference_number: paymentForm.reference_number || '',
                executive_name: paymentForm.executive_name || '',
                branch_name: paymentForm.branch_name || '',
                notes: paymentForm.notes || 'Payment Received',
            };

            if (activeTab === 'sales') {
                payload.product = 'Payment Received';
                payload.quantity = 0; // For sales transaction API
                await clientsAPI.addTransaction(payload);
            } else {
                payload.product = 'Payment Made';
                payload.quantity = 0; // For vendor transaction API
                payload.items = []; // For vendor transaction API
                await vendorsAPI.addTransaction(payload);
            }

            // Keep modal open — reset to client search state for next entry
            setSelectedParty(null);
            setPartySearch('');
            setPaymentForm({
                transaction_type: 'payment',
                payment_mode: 'cash',
                transaction_date: new Date().toISOString().split('T')[0],
                amount_paid: '',
                account_paid: '',
                reference_number: '',
                notes: '',
                executive_name: '',
                branch_name: ''
            });
            fetchPayments();
        } catch (error) {
            console.error("Error saving payment:", error);
            alert("Failed to save payment. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const handleEditClick = (payment) => {
        setEditingId(payment.id);
        setEditForm({
            amount_paid: payment.amount_paid || '',
            account_paid: payment.account_paid || '',
            reference_number: payment.reference_number || '',
            notes: payment.notes || ''
        });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({ amount_paid: '', account_paid: '', reference_number: '', notes: '' });
    };

    const handleSaveEdit = async (paymentId) => {
        try {
            if (!editForm.amount_paid || isNaN(editForm.amount_paid) || Number(editForm.amount_paid) <= 0) {
                alert("Please enter a valid amount.");
                return;
            }

            setLoading(true);

            // Find the original payment
            const originalPayment = payments.find(p => p.id === paymentId);
            if (!originalPayment) return;

            const payload = {
                ...originalPayment,
                amount_paid: Number(editForm.amount_paid),
                account_paid: editForm.account_paid || '',
                reference_number: editForm.reference_number || '',
                notes: editForm.notes || ''
            };

            if (activeTab === 'sales') {
                await clientsAPI.updateTransaction(paymentId, payload);
            } else {
                await vendorsAPI.updateTransaction(paymentId, payload);
            }

            alert("Payment updated successfully!");
            setEditingId(null);
            fetchPayments();
        } catch (error) {
            console.error("Error updating payment:", error);
            alert("Failed to update payment. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="payment-management-container">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2>Payment Management</h2>
                    <p className="text-muted">Track and filter all your business payments in one place.</p>
                </div>
                <button
                    className="add-return-btn"
                    onClick={() => {
                        setPartySearch('');
                        setSelectedParty(null);
                        fetchParties(activeTab);
                        setShowPaymentFormModal(true);
                    }}
                    style={{ backgroundColor: '#2e7d32', color: 'white', padding: '10px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    + Enter {activeTab === 'sales' ? 'Receipt' : 'Payment'}
                </button>
            </div>

            <div className="payment-tabs">
                <button
                    className={`tab-btn ${activeTab === 'sales' ? 'active' : ''}`}
                    onClick={() => handleTabChange('sales')}
                >
                    Sales Payments
                </button>
                <button
                    className={`tab-btn ${activeTab === 'purchase' ? 'active' : ''}`}
                    onClick={() => handleTabChange('purchase')}
                >
                    Purchase Payments
                </button>
            </div>

            <div className="filters-card">

                <div className="filters-grid">
                    <div className="filter-item">
                        <label>{activeTab === 'sales' ? 'Customer' : 'Vendor'} Name</label>
                        <input
                            type="text"
                            className="filter-input"
                            placeholder={`Search ${activeTab === 'sales' ? 'customer' : 'vendor'}...`}
                            value={customerName}
                            onChange={(e) => { setCustomerName(e.target.value); setPage(1); }}
                        />
                    </div>
                    <div className="filter-item">
                        <label>From Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                        />
                    </div>
                    <div className="filter-item">
                        <label>To Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                        />
                    </div>
                    <div className="filter-item">
                        <label>Payment Type</label>
                        <select
                            className="filter-input"
                            value={paymentType}
                            onChange={(e) => { setPaymentType(e.target.value); setPage(1); }}
                        >
                            <option value="all">All Types</option>
                            <option value="payment">Payment</option>
                            <option value="sale">Sale</option>
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="cheque">Cheque</option>
                            <option value="upi">UPI</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>Executive Officer</label>
                        <select
                            className="filter-input"
                            value={executiveName}
                            onChange={(e) => { setExecutiveName(e.target.value); setPage(1); }}
                        >
                            <option value="">All Officers</option>
                            {executives.map(exec => (
                                <option key={exec.id} value={exec.name}>{exec.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>Branch</label>
                        <select
                            className="filter-input"
                            value={branchName}
                            onChange={(e) => { setBranchName(e.target.value); setPage(1); }}
                        >
                            <option value="">All Branches</option>
                            {branches.map(branch => (
                                <option key={branch.id} value={branch.name}>{branch.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <button className="clear-btn" onClick={clearFilters} style={{ width: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="bi bi-x-circle me-2"></i>Clear
                        </button>
                    </div>
                </div>
            </div>

            <div className="table-card">
                {selectedIds.length > 0 && (
                    <div style={{ padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #eee' }}>
                        <span style={{ fontWeight: '500', color: '#555' }}>{selectedIds.length} selected</span>
                        <button
                            className="clear-btn"
                            onClick={handleDeleteSelected}
                            disabled={loading}
                            style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', width: '140px' }}
                        >
                            <i className="bi bi-trash"></i>Delete Selected
                        </button>
                    </div>
                )}
                <div className="table-responsive">
                    <table className="custom-table">
                        <thead>
                            <tr>
                                <th style={{ width: '40px', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={payments.length > 0 && selectedIds.length === payments.length}
                                    />
                                </th>
                                <th>Date</th>
                                <th>{activeTab === 'sales' ? 'Customer' : 'Vendor'} Name</th>
                                <th>Type</th>
                                <th>Reference</th>
                                <th>Executive</th>
                                <th>Branch</th>
                                <th>Amount Paid</th>
                                <th>Paid Account</th>
                                <th style={{ textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : payments.length > 0 ? (
                                payments.map((p) => (
                                    <tr key={p.id} className={selectedIds.includes(p.id) ? 'selected-row' : ''}>
                                        <td style={{ textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(p.id)}
                                                onChange={() => handleSelectRow(p.id)}
                                            />
                                        </td>
                                        <td>{formatDate(p.transaction_date)}</td>
                                        <td className="fw-bold">{p.client_name || 'N/A'}</td>
                                        <td>
                                            <span className={`type-badge ${(p.transaction_type || '').toLowerCase()}`}>
                                                {p.transaction_type?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td>{p.reference_number || '-'}</td>
                                        <td>{p.display_executive_name || p.executive_name || 'N/A'}</td>
                                        <td>{p.branch_name || 'N/A'}</td>
                                        
                                        {/* Amount Paid Column */}
                                        <td className="fw-bold text-success">
                                            {editingId === p.id ? (
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    value={editForm.amount_paid}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, amount_paid: e.target.value }))}
                                                    style={{ width: '100px' }}
                                                    placeholder="0.00"
                                                />
                                            ) : (
                                                formatCurrency(p.amount_paid)
                                            )}
                                        </td>

                                        {/* Paid Account Column */}
                                        <td>
                                            {editingId === p.id ? (
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={editForm.account_paid}
                                                    onChange={(e) => setEditForm(prev => ({ ...prev, account_paid: e.target.value }))}
                                                    style={{ width: '130px' }}
                                                >
                                                    <option value="">Select Account</option>
                                                    {companyAccounts.map(acc => (
                                                        <option key={acc.id} value={acc.name}>{acc.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                p.account_paid || ''
                                            )}
                                        </td>
                                        
                                        {/* Actions Column */}
                                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            {editingId === p.id ? (
                                                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                                    <button 
                                                        className="btn btn-sm btn-success" 
                                                        onClick={() => handleSaveEdit(p.id)}
                                                        title="Save"
                                                    >
                                                        <i className="bi bi-check2"></i>
                                                    </button>
                                                    <button 
                                                        className="btn btn-sm btn-secondary" 
                                                        onClick={handleCancelEdit}
                                                        title="Cancel"
                                                    >
                                                        <i className="bi bi-x"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <button 
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => handleEditClick(p)}
                                                    title="Edit Payment"
                                                >
                                                    <i className="bi bi-pencil"></i> Edit
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="9">
                                        <div className="empty-state">
                                            <i className="bi bi-inbox"></i>
                                            No payments found matching your criteria.
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="pagination-area">
                        <div className="pagination-info">
                            Showing {payments.length} of {totalRecords} records
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="page-btn"
                                disabled={page === 1}
                                onClick={() => setPage(page - 1)}
                            >
                                <i className="bi bi-chevron-left"></i>
                            </button>

                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    className={`page-btn ${page === i + 1 ? 'active' : ''}`}
                                    onClick={() => setPage(i + 1)}
                                >
                                    {i + 1}
                                </button>
                            ))}

                            <button
                                className="page-btn"
                                disabled={page === totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                <i className="bi bi-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showPaymentFormModal && (
                <div className="modal-overlay" onClick={() => { setShowPaymentFormModal(false); setSelectedParty(null); setPartySearch(''); }}>
                    <div className="modal-content form-modal large-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1e3c72, #2a5298)', color: '#fff', borderRadius: '10px 10px 0 0' }}>
                            <div>
                                <h3 style={{ margin: 0, color: '#fff', fontSize: '18px' }}>
                                    {activeTab === 'sales' ? '💰 Enter Receipt' : '💸 Enter Payment'}
                                </h3>
                                {selectedParty && (
                                    <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '2px' }}>
                                        {selectedParty.name}
                                        {selectedParty.executive_officer_name && ` · ${selectedParty.executive_officer_name}`}
                                    </div>
                                )}
                            </div>
                            <button className="modal-close" style={{ color: '#fff', fontSize: '22px' }} onClick={() => { setShowPaymentFormModal(false); setSelectedParty(null); setPartySearch(''); }}>×</button>
                        </div>

                        <div className="modal-body" style={{ padding: '0' }}>
                            {/* ---- CLIENT SEARCH (always visible at top) ---- */}
                            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0', background: selectedParty ? '#f8fffe' : '#fff' }}>
                                <label style={{ fontSize: '12px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px', display: 'block' }}>
                                    {activeTab === 'sales' ? 'Customer' : 'Vendor'}
                                </label>
                                {selectedParty ? (
                                    /* Selected state - show chip with change button */
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                        <div style={{ background: '#e8f5e9', border: '1.5px solid #4caf50', borderRadius: '8px', padding: '8px 14px', flex: 1 }}>
                                            <div style={{ fontWeight: 700, color: '#1b5e20', fontSize: '15px' }}>{selectedParty.name}</div>
                                            <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>
                                                {selectedParty.address && <span>📍 {selectedParty.address}</span>}
                                                {selectedParty.executive_officer_name && <span style={{ marginLeft: '10px', color: '#2563eb' }}>👤 {selectedParty.executive_officer_name}</span>}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => { setSelectedParty(null); setPartySearch(''); }}
                                            style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', cursor: 'pointer', color: '#555', whiteSpace: 'nowrap' }}
                                        >
                                            ✏️ Change
                                        </button>
                                    </div>
                                ) : (
                                    /* Search state */
                                    <div>
                                        <div style={{ position: 'relative', marginBottom: '8px' }}>
                                            <input
                                                type="text"
                                                autoFocus
                                                placeholder={`Search ${activeTab === 'sales' ? 'customer' : 'vendor'} by name...`}
                                                value={partySearch}
                                                onChange={(e) => setPartySearch(e.target.value)}
                                                style={{ width: '100%', padding: '10px 36px 10px 12px', border: '1.5px solid #4facfe', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                                            />
                                            <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', pointerEvents: 'none' }}>🔍</span>
                                        </div>
                                        {loadingParties ? (
                                            <div style={{ textAlign: 'center', padding: '12px', color: '#999', fontSize: '13px' }}>Loading...</div>
                                        ) : (
                                            <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '8px' }}>
                                                {filteredParties.length === 0 ? (
                                                    <div style={{ textAlign: 'center', padding: '20px', color: '#aaa', fontSize: '13px' }}>No results found</div>
                                                ) : (
                                                    filteredParties.map(party => (
                                                        <div
                                                            key={party.id}
                                                            onClick={() => {
                                                                setSelectedParty(party);
                                                                setPaymentForm(prev => ({
                                                                    ...prev,
                                                                    executive_name: party.executive_officer_name || prev.executive_name || ''
                                                                }));
                                                            }}
                                                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid #f5f5f5', cursor: 'pointer', transition: 'background 0.15s' }}
                                                            onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                                                            onMouseLeave={e => e.currentTarget.style.background = ''}
                                                        >
                                                            <div>
                                                                <div style={{ fontWeight: 600, fontSize: '14px', color: '#1a1a2e' }}>{party.name}</div>
                                                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '2px' }}>
                                                                    {party.address && <span style={{ fontSize: '12px', color: '#777' }}>📍 {party.address}</span>}
                                                                    {party.executive_officer_name && <span style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }}>👤 {party.executive_officer_name}</span>}
                                                                </div>
                                                            </div>
                                                            <span style={{ color: '#4facfe', fontSize: '18px', paddingLeft: '8px' }}>→</span>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '11px', color: '#aaa', marginTop: '5px', textAlign: 'right' }}>{filteredParties.length} results</div>
                                    </div>
                                )}
                            </div>

                            {/* ---- PAYMENT FIELDS (only when client selected) ---- */}
                            {selectedParty && (
                                <div className="form-grid" style={{ padding: '16px 20px' }}>
                                    <div className="form-group">
                                        <label>Amount {activeTab === 'sales' ? 'Received' : 'Paid'} (₹)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            autoFocus
                                            value={paymentForm.amount_paid}
                                            onChange={(e) => setPaymentForm({ ...paymentForm, amount_paid: e.target.value })}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Paid To / Received In (Account)</label>
                                        <select
                                            className="form-control"
                                            value={paymentForm.account_paid}
                                            onChange={(e) => setPaymentForm({ ...paymentForm, account_paid: e.target.value })}
                                        >
                                            <option value="">Select Account</option>
                                            {companyAccounts.map(acc => (
                                                <option key={acc.id} value={acc.name}>{acc.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={paymentForm.transaction_date}
                                            onChange={(e) => setPaymentForm({ ...paymentForm, transaction_date: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Executive Officer</label>
                                        <select
                                            className="form-control"
                                            value={paymentForm.executive_name}
                                            onChange={(e) => setPaymentForm({ ...paymentForm, executive_name: e.target.value })}
                                        >
                                            <option value="">Select Executive</option>
                                            {executives.map(exec => (
                                                <option key={exec.id} value={exec.name}>{exec.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Notes / Remarks</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={paymentForm.notes}
                                            onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-cancel" onClick={() => { setShowPaymentFormModal(false); setSelectedParty(null); setPartySearch(''); }}>Close</button>
                            <button
                                className="btn btn-save"
                                onClick={handleSavePayment}
                                disabled={loading || !selectedParty || !paymentForm.amount_paid}
                                title={!selectedParty ? 'Please select a client first' : ''}
                            >
                                {loading ? 'Saving...' : `Save ${activeTab === 'sales' ? 'Receipt' : 'Payment'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentManagement;
